/** One scope's notes with the actions the viewer is allowed: read, delete one
 * (AlertDialog), clear all (AlertDialog). Used for a member's own notes, the
 * workspace notes and, for admins, another member's notes. */
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../shared/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../shared/ui/alert-dialog';
import { botMemoryService, type BotMemoryScope, type MemoryItem } from '../../../services/BotMemoryService';
import { BotMemoryNotesList } from './BotMemoryNotesList';
import { NOTES_FULL, REDACTION_INCOMPLETE, pathParts } from './botMemoryFormat';
import { useBotMemoryNotes } from './useBotMemory';

interface BotMemoryScopeViewProps {
  workspaceId: string;
  scope: BotMemoryScope;
  /** Whose notes these are, for the pending and clear wording. */
  owner: 'you' | 'member' | 'workspace';
  canDelete: boolean;
  onEdit?: (note: MemoryItem) => void;
  oversized?: boolean;
  emptyText: string;
  /** Bumped by the parent after it saves a note, to refetch. */
  reloadKey?: number;
  /** Fires after any delete or clear, so summaries can refresh. */
  onChanged?: () => void;
}

const destructive = 'bg-destructive hover:bg-destructive/90 text-destructive-foreground';

export function BotMemoryScopeView({ workspaceId, scope, owner, canDelete, onEdit, oversized, emptyText, reloadKey, onChanged }: BotMemoryScopeViewProps) {
  const { notes, truncated, clearPending, loading, failed, drop, emptied } = useBotMemoryNotes(workspaceId, scope, reloadKey);
  const [deleting, setDeleting] = useState<MemoryItem | null>(null);
  const [clearing, setClearing] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const confirmDelete = async () => {
    const target = deleting;
    if (!target) return;
    setDeleting(null);
    setBusyId(target.id);
    try {
      const result = await botMemoryService.remove(workspaceId, scope, target.id);
      drop(target.id);
      if (result.redactionComplete) toast.success('Note deleted'); else toast.warning(REDACTION_INCOMPLETE);
      onChanged?.();
    } catch (err) { toast.error(err instanceof Error ? err.message : 'The note could not be deleted.'); }
    finally { setBusyId(null); }
  };
  const confirmClear = async () => {
    setClearing(false);
    try {
      const result = await botMemoryService.clear(workspaceId, scope);
      emptied(result.status === 'deferred');
      if (result.status === 'deferred') toast.message(owner === 'you' ? 'Clearing once your running tasks finish' : 'Clearing once running tasks finish');
      else toast.success('Notes cleared');
      onChanged?.();
    } catch (err) { toast.error(err instanceof Error ? err.message : 'The notes could not be cleared.'); }
  };

  if (loading) return <p role="status" className="text-sm text-muted-foreground">Loading</p>;
  if (failed) return <p role="alert" className="text-sm text-destructive">The notes could not be loaded.</p>;

  return (
    <div className="space-y-2">
      {oversized && <p className="text-xs text-muted-foreground">{NOTES_FULL}</p>}
      {clearPending && <p role="status" className="text-xs text-muted-foreground">{owner === 'you' ? 'Clearing once your running tasks finish.' : 'Clearing once running tasks finish.'}</p>}
      <BotMemoryNotesList notes={notes} emptyText={emptyText} busyId={busyId} onEdit={onEdit}
        onDelete={canDelete ? setDeleting : undefined}
        load={async (note) => (await botMemoryService.read(workspaceId, scope, note.id)).content} />
      {truncated && <p className="text-xs text-muted-foreground">Showing the first 200 notes.</p>}
      {canDelete && notes.length > 0 && !clearPending && (
        <div className="flex justify-end"><Button size="sm" variant="ghost" className="text-destructive" onClick={() => setClearing(true)}>Clear all</Button></div>
      )}

      <AlertDialog open={deleting !== null} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting ? `${pathParts(deleting.path).folder}${pathParts(deleting.path).title}` : ''} is removed and bots will no longer read it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmDelete()} className={destructive}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={clearing} onOpenChange={setClearing}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all notes?</AlertDialogTitle>
            <AlertDialogDescription>
              {owner === 'you' ? 'Every note bots have kept about working with you is removed.' : owner === 'member' ? 'Every note kept for this member is removed.' : 'Every workspace note is removed and bots stop following them.'} This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmClear()} className={destructive}>Clear all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
